import type { ComponentType } from "react";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "goober";
import { Card } from "clutch/src/Card/Card.jsx";
import { mobile } from "clutch/src/Style/style.mjs";

import { GAME_NAME_MAP, GAME_SHORT_NAMES } from "@/app/constants.mjs";
import type { GameMeta } from "@/app/game-definition.mjs";
// import { BodyDownloadButton } from "@/app/DownloadButtonWrapper.jsx";
import gamesList from "@/app/games.mjs";
import ContentfulArticleGrid from "@/marketing/ContentfulArticleGrid.jsx";
import ContentfulArticles from "@/marketing/ContentfulArticles.jsx";
import GameArticles from "@/marketing/GameArticles.jsx";
import GameFeatures from "@/marketing/GameFeatures.jsx";
import { PAGE_DEFINITION as META_DEFINITION } from "@/marketing/home-constants.mjs";
import homeRefs from "@/marketing/HomeGamePage.refs.jsx";
import SearchBar from "@/search/SearchBar.jsx";
import GameNameIcon from "@/shared/GameNameIcon.jsx";
import downloadApp, { useDownloadLink } from "@/util/download-app.mjs";
import { useGameSymbol } from "@/util/game-route.mjs";
import { useSnapshot } from "@/util/use-snapshot.mjs";

const HomeLayout = styled("div")`
  display: grid;
  gap: var(--sp-18);
  padding-block: var(--sp-14);

  @container (inline-size <= 800px) {
    gap: var(--sp-12);
    padding-block: var(--sp-8);
  }

  @container (inline-size <= 600px) {
    gap: var(--sp-8);
    padding-block: 0;
  }

  .bg-video,
  .bg-image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 44rem;
    object-fit: cover;
    mask-image: linear-gradient(to bottom, hsla(0deg 0% 0% / 0.2), transparent);
    z-index: -1;
    pointer-events: none;

    @container (inline-size <= 800px) {
      height: 32rem;
    }
    @container (inline-size <= 600px) {
      height: 24rem;
    }
  }

  > * {
    animation: home-enter ease-out both;
    animation-timeline: view();
    animation-range: entry 0% entry 50%;
  }

  @keyframes home-enter {
    0% {
      scale: 0.9;
      opacity: 0;
    }
  }
`;

const GameGuidesSection = styled("section")`
  container-type: inline-size;
  container-name: game-guides;

  .guides {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
    gap: var(--sp-3);
    width: 100%;
    max-width: var(--sp-container);
    margin-inline: auto;

    @container game-guides (inline-size <= 800px) {
      grid-template-columns: 1fr;
    }
  }
`;

type ContentfulArticle = {
  id: string;
  title: string;
  excerpt: string;
};

const Banner = styled("div")`
  container-type: inline-size;
  container-name: game-search;

  .content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--sp-6);
    width: 100%;
    padding: 0 var(--page-padding);

    ${mobile} {
      padding: var(--sp-8) 0;

      .download-blitz-button {
        display: none;
      }
    }
  }

  .search-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--sp-6);
    width: fit-content;

    .game-title {
      display: flex;
      align-items: center;
      gap: var(--sp-3);
    }
  }

  label[for="home-search-input"] {
    outline: 0px solid transparent;
    transition: outline var(--transition);

    &:focus-within {
      outline: 4px solid var(--shade0-15);
    }

    #home-search-input {
      background-color: var(--shade0);

      &::placeholder {
        color: var(--shade5);
      }
    }
  }

  .counts {
    display: flex;
    align-items: center;
    gap: var(--sp-4);

    [data-has-players="false"] {
      visibility: hidden;
    }
    [data-has-matches="false"] {
      visibility: hidden;
    }

    ${mobile} {
      > *:not(:nth-child(1)) {
        display: none;
      }
    }

    .dot {
      display: block;
      width: var(--sp-1);
      height: var(--sp-1);

      background: var(--shade4);
      border-radius: 50%;
    }

    p {
      display: flex;
      align-items: center;
      gap: var(--sp-1);

      color: var(--shade0);

      white-space: pre-wrap;

      svg {
        width: var(--sp-7);
        height: var(--sp-7);
      }

      span {
        color: var(--shade0-75);
      }
    }
  }
`;

export type ContentfulFeatures = {
  type: "contentful-grid" | "contentful-articles";
  title?: Translation;
  description?: Translation;
  linkConstructor?: (article: ContentfulArticle) => string;
  cta?: {
    title: Translation;
    href: string;
  };
  tags?: string[];
};

export const BlitzFeatures = {
  Search: "blitz-search",
  SearchHidden: "blitz-search-hidden",
  Guides: "blitz-guides",
  Articles: "blitz-articles",
  GameFeatures: "blitz-game-features",
} as const;

export type ContentfulGameConfig =
  | (typeof BlitzFeatures)[keyof typeof BlitzFeatures]
  | ContentfulFeatures
  | (() => Promise<React.JSX.Element>);

export default function HomeGamePage() {
  const gameSymbol = useGameSymbol();
  const shortName = GAME_SHORT_NAMES[gameSymbol];
  const metaDefinition = META_DEFINITION[gameSymbol];
  const gameDefinition = gamesList.find((i) => i.gameSymbol === gameSymbol);
  if (!metaDefinition || !gameDefinition) return null;

  // @ts-expect-error - This has been assigned to all game, page and "any" definitions - unsure why its erroring like it doesn't exist
  const { homepageFeatures } = gameDefinition;

  if (!Array.isArray(homepageFeatures)) {
    return (
      <HomeLayout>
        {metaDefinition.bgVideo ? (
          <video
            src={metaDefinition.bgVideo}
            autoPlay
            muted
            loop
            playsInline
            className="bg-video"
          />
        ) : metaDefinition.bg ? (
          <img src={metaDefinition.bg} alt="" className="bg-image" />
        ) : null}
        <Search />
        <GameArticles articles={metaDefinition.articles} game={gameSymbol} />
        <GameGuides />
        {!!metaDefinition.features?.length && (
          <GameFeatures features={metaDefinition.features} />
        )}
      </HomeLayout>
    );
  }

  return (
    <HomeLayout>
      {metaDefinition.bgVideo ? (
        <video
          src={metaDefinition.bgVideo}
          autoPlay
          muted
          loop
          playsInline
          className="bg-video"
        />
      ) : metaDefinition.bg ? (
        <img src={metaDefinition.bg} alt="" className="bg-image" />
      ) : null}
      {homepageFeatures.map((i, key) => {
        // No immediate use for Custom UI components for now
        // if (typeof i === "function") {
        //   return React.createElement(i());
        // }
        switch (typeof i === "string" ? i : i.type) {
          case "contentful-articles": {
            return (
              <ContentfulArticles
                key={key}
                {...i}
                tags={["news"]}
                linkConstructor={(article) =>
                  `/${shortName}/article/${article.id}`
                }
              />
            );
          }
          case "contentful-grid": {
            return (
              <ContentfulArticleGrid
                key={key}
                {...i}
                linkConstructor={(article) =>
                  `/${shortName}/article/${article.id}`
                }
              />
            );
          }
          case BlitzFeatures.Search: {
            return <Search key={key} />;
          }
          case BlitzFeatures.SearchHidden: {
            return <Search key={key} hidden />;
          }
          case BlitzFeatures.Articles: {
            return (
              <GameArticles
                key={key}
                articles={metaDefinition.articles}
                game={gameSymbol}
              />
            );
          }
          case BlitzFeatures.Guides: {
            return <GameGuides key={key} />;
          }
          case BlitzFeatures.GameFeatures: {
            if (!metaDefinition.features?.length) return null;
            return (
              <GameFeatures key={key} features={metaDefinition.features} />
            );
          }
        }
        return null;
      })}
    </HomeLayout>
  );
}

function Search({ hidden }: { hidden?: boolean }) {
  const gameSymbol = useGameSymbol();
  const { t } = useTranslation();
  const gameName = t(...GAME_NAME_MAP[gameSymbol]);

  return (
    <Banner>
      <div className="content">
        <div className="search-section">
          <h1 className="game-title type-h5">
            <GameNameIcon gameSymbol={gameSymbol} />
          </h1>

          {!hidden && (
            <SearchBar
              /** We want the search bar to steal focus when switching between games */
              key={gameName}
              id="home-search-input"
              placeholder={t("home:searchGame", "Search {{gameName}}", {
                gameName,
              })}
            />
          )}
          {/* <BodyDownloadButton
            isPrimary={true}
            size="large"
            textClass="type-form--button"
            target="_blank"
            className="download-blitz-button body"
          /> */}
          <DownloadButton gameName={gameName} />
        </div>
      </div>
    </Banner>
  );
}

function DownloadButton({ gameName }: { gameName: string }) {
  const { t } = useTranslation();
  const downloadUrl = useDownloadLink();

  return (
    <DownloadLink
      href={downloadUrl}
      className="download-blitz-button"
      onClick={(e) => {
        downloadApp(e, `homepage-${gameName}`);
      }}
    >
      {t("common:downloadBlitzForGameName", "Download Blitz for {{gameName}}", {
        gameName,
      })}
    </DownloadLink>
  );
}

const DownloadLink = styled("a")`
  --radii: var(--br-xl);
  position: relative;
  background: linear-gradient(
    346deg,
    oklch(0.51 0.23 16.94),
    oklch(0.65 0.3 17.98)
  );
  color: white;
  font-weight: 700;
  line-height: 1;
  padding: 1rem 1.5rem;
  border-radius: var(--radii);
  transition:
    scale var(--transition),
    box-shadow var(--transition);

  &:hover {
    scale: 1.05;
    box-shadow: 0 0 0 4px hsl(0deg 0% 100% / 12%);
  }
`;

function GameGuides() {
  const { guides: $guides } = useSnapshot(homeRefs);
  const gameSymbol = useGameSymbol();
  const guides = useMemo(
    () => $guides[gameSymbol] as GameMeta["guides"],
    [$guides, gameSymbol],
  );
  if (!guides) return null;
  return (
    <GameGuidesSection>
      <div className="guides">
        {!!guides.length && <LazyComponents lazyComponents={guides} />}
      </div>
    </GameGuidesSection>
  );
}

/**
 * Blitz compatible Lazy / Suspense loader for SSR
 *
 * Using React.lazy + Suspense is bad for Blitz since our SSR implementation doesn't like it
 * The below implementation is similar to the Match Tiles V2 in implementation details
 */
const LazyComponents: React.FC<{ lazyComponents: GameMeta["guides"] }> = ({
  lazyComponents,
}) => {
  const [components, setComponents] = useState<ComponentType<unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const loadComponents = async () => {
      try {
        const resolvedComponents = await Promise.all(
          lazyComponents.map(async (loader) => {
            const module = await loader();
            return module.default;
          }),
        );
        setComponents(resolvedComponents);
        setLoading(false);
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error("Failed to load components"),
        );
        setLoading(false);
      }
    };

    loadComponents();
  }, [lazyComponents]);

  if (loading) {
    return (
      <>
        <LoadingGuide />
        <LoadingGuide />
      </>
    );
  }

  // No error fallback yet but not entirely necessary since Game Definitions are strongly typed
  // It would be a fatal error for a developer to purposely and incorrectly put bad import statements in the game guides
  if (error) return null;

  return (
    <>
      {components.map((Component, index) => (
        <Component key={index} />
      ))}
    </>
  );
};

function LoadingGuide() {
  return (
    <Card
      className="leader-card loading-row"
      padding={0}
      style={{ height: 425 }}
    />
  );
}
